import { getProvider } from './providers';
import {
  CONDITIONAL_PROPERTIES,
  NATURES,
  OPTIONAL_PROPERTIES,
  REQUIRED_PROPERTIES,
} from './taxonomy';

const IS_DEV = process.env.NODE_ENV !== 'production';

// Single source of truth — imported by GlobalProvider for DevToolbar rendering.
export const TOOLBAR_HOSTS = ['localhost', 'release.promobit.com.br'];

const extractNature = (eventName) => {
  const firstWord = eventName.split(' ')[0];

  return NATURES.includes(firstWord) ? firstWord : null;
};

const validateRequiredProperties = (nature, properties) => {
  const required = REQUIRED_PROPERTIES[nature];
  const errors = [];

  for (const [key, allowedValues] of Object.entries(required)) {
    if (!(key in properties)) {
      errors.push(`Missing required property "${key}" for "${nature}" event`);
      continue;
    }

    if (allowedValues !== null && !allowedValues.includes(properties[key])) {
      errors.push(
        `Invalid value "${properties[key]}" for "${key}". Allowed: ${allowedValues.join(', ')}`
      );
    }
  }

  return errors;
};

const validateOptionalProperties = (nature, properties) => {
  const optional = OPTIONAL_PROPERTIES[nature];

  if (!optional) return [];

  const errors = [];

  for (const [key, allowedValues] of Object.entries(optional)) {
    if (!(key in properties)) continue;

    if (allowedValues !== null && !allowedValues.includes(properties[key])) {
      errors.push(
        `Invalid value "${properties[key]}" for "${key}". Allowed: ${allowedValues.join(', ')}`
      );
    }
  }

  return errors;
};

const validateConditionalProperties = (nature, properties) => {
  const conditionals = CONDITIONAL_PROPERTIES[nature];

  if (!conditionals) {
    return [];
  }

  const errors = [];

  for (const [key, rule] of Object.entries(conditionals)) {
    const conditionMet =
      'notValue' in rule.when
        ? properties[rule.when.field] !== rule.when.notValue
        : properties[rule.when.field] === rule.when.value;

    if (conditionMet && !(key in properties)) {
      const condition =
        'notValue' in rule.when
          ? `${rule.when.field} is not "${rule.when.notValue}"`
          : `${rule.when.field} is "${rule.when.value}"`;

      errors.push(`Missing "${key}" — required when ${condition}`);
    }
  }

  return errors;
};

/**
 * Validated PostHog event tracking.
 * Only allowed way to fire PostHog events.
 *
 * Non-production: throws on validation errors.
 * Production: warns but still fires the event.
 *
 * @param {string} eventName - Name starting with a nature
 * @param {object} properties - Event properties
 */
const trackEvent = (eventName, properties = {}) => {
  const nature = extractNature(eventName);

  if (!nature) {
    const msg = `[PostHog] Event name must start with: ${NATURES.join(', ')}. Got: "${eventName}"`;

    if (IS_DEV) {
      throw new Error(msg);
    }

    // eslint-disable-next-line no-console
    console.warn(msg);
  }

  if (nature) {
    const requiredErrors = validateRequiredProperties(nature, properties);
    const optionalErrors = validateOptionalProperties(nature, properties);
    const conditionalErrors = validateConditionalProperties(nature, properties);
    const allErrors = [
      ...requiredErrors,
      ...optionalErrors,
      ...conditionalErrors,
    ];

    if (allErrors.length > 0) {
      const msg = `[PostHog] Validation errors for "${eventName}":\n${allErrors.join('\n')}`;

      if (IS_DEV) {
        throw new Error(msg);
      }

      // eslint-disable-next-line no-console
      console.warn(msg);
    }
  }

  // Buffer events in localStorage for the DevToolbar Events panel.
  // Active on dev and on toolbar-enabled hosts (e.g. release.promobit.com.br).
  // Runs before provider check so dropped events are visible for debugging.
  // Guard: SSR has no window/localStorage.
  const isToolbarHost =
    typeof window !== 'undefined' &&
    TOOLBAR_HOSTS.includes(window.location.hostname);

  if (IS_DEV || isToolbarHost) {
    try {
      const key = '__dev_posthog_events';
      const maxEvents = 100;
      const events = JSON.parse(localStorage.getItem(key) || '[]');

      events.unshift({
        name: eventName,
        props: properties,
        url: window.location.pathname,
        time: new Date().toLocaleTimeString('pt-BR', { hour12: false }),
      });

      if (events.length > maxEvents) events.length = maxEvents;
      localStorage.setItem(key, JSON.stringify(events));
    } catch {
      // localStorage unavailable — ignore
    }
  }

  // Intentional: in production, fire the event even after validation warnings.
  // Convention: never block user flows due to instrumentation errors. Invalid
  // events reach PostHog but are caught during dashboard analysis.
  const provider = getProvider();

  if (!provider) {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[PostHog] Event "${eventName}" dropped — SDK not initialized yet`
      );
    }
    return;
  }

  provider.track(eventName, properties);
};

export default trackEvent;
